import React from "react";
import { useTranslation } from "react-i18next";
import { HeaderMainTitle } from "../Common/HeaderMainTitle";
import { Box, Typography, Switch, CircularProgress, Link } from "@mui/material";
import { getClientId } from "../utils";
import { useUpdateClient } from "../../../hooks/api/useUpdateClient";
import { useGlobalToast } from "../../GlobalToastProvider";
import { useGetClientById } from "../../../hooks/api/useGetClientById";
import { useGetCustomerSignedForm } from "../../../hooks/api/useGetCustomerSignedForm";
import useToggleFormSkippedStatus from "../../ClientDashboard/hooks/useToggleFormSkippedStatus";
import { Keyhole } from "@phosphor-icons/react";

interface DrawerDataPrivacyProps {
  onBack: () => void;
  user: any;
}

export const DrawerDataPrivacy: React.FC<DrawerDataPrivacyProps> = ({ onBack, user }) => {
  const { t } = useTranslation(["clientHeader"]);
  const { showToast } = useGlobalToast();
  const clientId = getClientId(user);

  const { data: clientResponse, isFetching: clientFetching, refetch: refetchGetClientById } = useGetClientById(clientId);

  const { customerSignedForm, isFetching: isFetchingCustomerSignedForm } = useGetCustomerSignedForm(
    { clientId, type: "RESEARCH_CONSENT" }
  );

  const { mutate: skipMutate, isLoading: skipIsLoading } = useToggleFormSkippedStatus(clientId);

  const researchParticipation = clientResponse?.data?.agreeToResearchParticipation || false;

  const { updateClientMutation, isLoadingUpdateClient } = useUpdateClient({
    onSuccess: () => {
      showToast({ message: t("clientHeader:dataPrivacy.updateSuccess") });
      refetchGetClientById();
    },
  });

  const handleResearchParticipationChange = () => {
    if (customerSignedForm?.skipped && customerSignedForm?.pkey) {
      skipMutate({ formId: customerSignedForm?.pkey, skipped: false });
    } else {
      updateClientMutation({
        clientId,
        data: { agree_to_research_participation: !researchParticipation },
      });
    }
  };

  const isLoading = clientFetching && !clientResponse;
  const hasError = !clientResponse?.data && !clientFetching;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("clientHeader:dataPrivacy.unableToLoad")}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ padding: "0 24px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("clientHeader:dataPrivacy.description")}{" "}
            <Link href="https://www.getanswersnow.com/privacy" target="_blank" rel="noopener noreferrer">
              {t("clientHeader:dataPrivacy.learnMore")}
            </Link>
          </Typography>
        </Box>

        <Box sx={{ padding: "0 24px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              py: 2,
            }}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                {t("clientHeader:dataPrivacy.researchParticipation")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("clientHeader:dataPrivacy.researchDescription")}
              </Typography>
            </Box>
            <Switch
              checked={researchParticipation}
              onChange={handleResearchParticipationChange}
              color="primary"
              disabled={
                isLoadingUpdateClient ||
                clientFetching ||
                skipIsLoading ||
                isFetchingCustomerSignedForm
              }
            />
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <HeaderMainTitle
        user={user}
        title={t("clientHeader:dataPrivacy.title")}
        onBack={onBack}
        icon={Keyhole}
      />
      {renderContent()}
    </>
  );
};
